import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import { Box, useTheme, Typography } from "@mui/material";
import { tokens } from "../../../theme";
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Axios from 'axios';
import { getAuthHeader } from '../../../utils/authToken';

const ClassMaterialDeleteModal = ({
    courseId,
    classMaterials,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertOpen,
    setErrorAlertMessage,
    setBackdropOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        maxHeight: '80vh',
        overflowY: 'auto',
        borderRadius: '8px',
    };

    const [selectedMaterials, setSelectedMaterials] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setSelectedMaterials([]);
        setOpen(false);
    };

    const handleDelete = async (event) => {
        event.preventDefault();

        if (selectedMaterials.length === 0) {
            setErrorAlertOpen(true);
            setErrorAlertMessage("Please select at least one class material to delete.");
            return;
        }

        setBackdropOpen(true);

        const promises = selectedMaterials.map(materialId => {
            return Axios.delete(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/class-materials/${materialId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });
        });

        Promise.all(promises)
            .then(() => {
                setSuccessAlertOpen(true);
                setSuccessAlertMessage("Selected class materials have been deleted successfully.");
                setSelectedMaterials([]);
            })
            .catch((error) => {
                setErrorAlertOpen(true);
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Error deleting class materials.");
            })
            .finally(() => {
                setBackdropOpen(false);
                handleClose();
                window.location.reload();
            });
    };


    return (
        <div>
            <Button
                type="submit"
                variant="contained"
                style={{
                    backgroundColor: colors.primary[300],
                    color: "white",
                    width: "14rem",
                    marginTop: "0.5rem",
                    minHeight: "3rem"
                }}
                size='small'
                onClick={handleOpen}
            >
                Delete Class Materials
            </Button>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                                Delete Class Materials
                            </Typography>
                            <Typography variant="body2" color={colors.primary[300]}>
                                Select the class materials you want to delete. This action cannot be undone.
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {classMaterials === null || classMaterials.length === 0 ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            minHeight: "8rem",
                                            gap: "0.4rem",
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ my: "auto" }} />
                                        <Typography variant="h6" color={colors.primary[450]} my="auto">
                                            No class materials available.
                                        </Typography>
                                    </Box>
                                ) : (
                                    classMaterials.map((material, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                border: '1px solid',
                                                borderColor: colors.primary[100],
                                                backgroundColor: colors.primary[700],
                                                padding: '0.5rem',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedMaterials.includes(material.id)}
                                                onChange={() => {
                                                    setSelectedMaterials(prev =>
                                                        prev.includes(material.id)
                                                            ? prev.filter(id => id !== material.id)
                                                            : [...prev, material.id]
                                                    );
                                                }}
                                                color="primary"
                                                sx={{ mr: 1 }}
                                            />
                                            <Typography
                                                sx={{
                                                    wordBreak: 'break-word',
                                                    flex: 1,
                                                }}
                                            >
                                                {material.name}
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                            <Button
                                type="button"
                                variant="contained"
                                color="error"
                                style={{
                                    marginTop: "0.5rem",
                                    width: "fit-content",
                                }}
                                onClick={(e) => handleDelete(e)}
                                disabled={selectedMaterials.length === 0}
                            >
                                Delete ({selectedMaterials.length})
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default ClassMaterialDeleteModal
